import { AxiosError } from 'axios';

export interface NormalizedCsvError {
  row?: number;
  column?: string;
  message: string;
}

export interface NormalizedCsvErrorResponse {
  errors: NormalizedCsvError[];
  message?: string;
}

// Attempt to coerce various backend CSV error shapes into a normalized list
// Supported shapes:
// 1. error.response.data.errors: Array<{ row|rowNumber, column|columnName, message|error|errorMessage }>
// 2. error.response.data: Array<same as above>
// 3. error.response.data: { message: string }
// 4. Fallback: use top-level error.message
export function normalizeCsvErrorResponse(error: AxiosError): NormalizedCsvErrorResponse {
  const result: NormalizedCsvErrorResponse = { errors: [] };
  const data: any = error?.response?.data;
  if (!data) {
    result.message = error.message;
    return result;
  }

  let candidateErrors: any[] | undefined;
  if (Array.isArray(data?.errors)) {
    candidateErrors = data.errors;
  } else if (Array.isArray(data)) {
    candidateErrors = data;
  } else if (Array.isArray(data?.problems)) {
    // Some APIs return { problems: [{ row, column, message }] }
    candidateErrors = data.problems;
  } else if (Array.isArray(data?.validationErrors)) {
    // Alternate key for validation error arrays
    candidateErrors = data.validationErrors;
  } else if (Array.isArray(data?.details)) {
    // Generic details array sometimes used for error lists
    candidateErrors = data.details;
  } else if (data?.problemReport && Array.isArray(data.problemReport?.errors)) {
    // Nested problem report: { problemReport: { errors: [...] } }
    candidateErrors = data.problemReport.errors;
  }

  if (candidateErrors && candidateErrors.length > 0) {
    result.errors = candidateErrors.map(e => {
      // Support multiple possible key names for row/column/message
      const row = e.row ?? e.rowNumber ?? e.lineNumber ?? e.index ?? e.rowIndex ?? e.location?.row;
      const column =
        e.column ??
        e.columnName ??
        e.field ??
        e.columnKey ??
        e.property ??
        e.location?.column ??
        e.location?.field;
      const message =
        e.message ?? e.errorMessage ?? e.error ?? e.description ?? e.reason ?? 'Invalid value';
      const normalizedRow = typeof row === 'number' ? row : undefined;
      const normalizedColumn = typeof column === 'string' ? column : undefined;
      return {
        row: normalizedRow,
        column: normalizedColumn,
        message,
      };
    });
  } else if (typeof data?.message === 'string') {
    result.message = data.message;
  } else if (typeof error.message === 'string') {
    result.message = error.message;
  } else {
    result.message = 'Error importing CSV';
  }

  return result;
}

export interface LegacyCsvError {
  field: string;
  message: string;
}

// Convert normalized errors to legacy field/message pair format expected by ImportCsvModal
export function toLegacyCsvErrors(errors: NormalizedCsvError[], limit: number): LegacyCsvError[] {
  const sliced = errors.slice(0, limit);
  return sliced.map(e => {
    let field = '';
    if (e.row != null && e.column) {
      field = `Row ${e.row}, ${e.column}`;
    } else if (e.row != null) {
      field = `Row ${e.row}`;
    } else if (e.column) {
      field = `Column: ${e.column}`;
    }
    return { field, message: e.message };
  });
}
